import { useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import {
  getCurrentUser,
  syncUserWithServer,
  updateUser,
} from "src/features/profile/state/profileSlice";
import type { IUser } from "src/model/model";
import type { SidebarItemDef, SidebarItemId } from "./sidebarConfig";

const readFavoriteIds = (user?: IUser): string[] => {
  const favorites = getUserPreferences({ currentUser: user })?.sidebarFavorites;
  return Array.isArray(favorites) ? favorites : [];
};

const sameOrder = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

export const useSidebarFavorites = (visibleItems: SidebarItemDef[]) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(getCurrentUser);
  const latestUserRef = useRef(currentUser);
  latestUserRef.current = currentUser;

  const [override, setOverride] = useState<string[] | null>(null);
  const [frozen, setFrozen] = useState<string[] | null>(null);
  const [pending, setPending] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const desiredRef = useRef<string[] | null>(null);
  const queuedRef = useRef<string[] | null>(null);
  const inFlightRef = useRef(false);

  const favoriteIds = override ?? frozen ?? readFavoriteIds(currentUser);
  const favoritableItems = visibleItems.filter((item) => item.favoritable);
  const favoriteItems = favoriteIds.flatMap((id) => {
    const item = favoritableItems.find((candidate) => candidate.id === id);
    return item ? [item] : [];
  });

  const settle = () => {
    inFlightRef.current = false;
    desiredRef.current = null;
    queuedRef.current = null;
    setOverride(null);
    setPending(false);
  };

  const resync = async () => {
    setResyncing(true);
    try {
      await dispatch(syncUserWithServer({})).unwrap();
    } catch (error) {
      console.warn("[useSidebarFavorites] failed to resync favorites", error);
    }
    setResyncing(false);
  };

  const send = async (next: string[], baseUser: IUser | undefined) => {
    if (!baseUser) {
      settle();
      return;
    }
    inFlightRef.current = true;
    setPending(true);
    let savedUser: IUser | undefined;
    try {
      savedUser = await dispatch(
        updateUser({
          id: baseUser.id,
          preferences: JSON.stringify({
            ...(getUserPreferences({ currentUser: baseUser }) ?? {}),
            sidebarFavorites: next,
          }),
        }),
      ).unwrap();
    } catch (error) {
      console.warn("[useSidebarFavorites] failed to save favorites", error);
    }
    if (!savedUser) {
      console.warn("[useSidebarFavorites] favorites were not saved");
      settle();
      void resync();
      return;
    }
    const queued = queuedRef.current;
    if (queued) {
      queuedRef.current = null;
      void send(queued, savedUser);
      return;
    }
    settle();
  };

  const currentIds = () => desiredRef.current ?? frozen ?? readFavoriteIds(latestUserRef.current);

  const commit = (next: string[]) => {
    desiredRef.current = next;
    setOverride(next);
    if (inFlightRef.current) {
      queuedRef.current = next;
      return;
    }
    void send(next, latestUserRef.current);
  };

  const withHiddenIds = (visibleOrder: string[], base: string[]) => [
    ...visibleOrder,
    ...base.filter((id) => !favoritableItems.some((item) => item.id === id)),
  ];

  const isFavorite = (id: SidebarItemId) => favoriteIds.includes(id);

  const toggleFavorite = (id: SidebarItemId) => {
    if (!favoritableItems.some((item) => item.id === id)) return;
    const base = currentIds();
    commit(base.includes(id) ? base.filter((favId) => favId !== id) : [...base, id]);
  };

  const setFavoritesOrder = (ids: SidebarItemId[]) => {
    if (
      sameOrder(
        ids,
        favoriteItems.map((item) => item.id),
      )
    )
      return;
    commit(withHiddenIds(ids, currentIds()));
  };

  const addFavoriteAt = (id: SidebarItemId, index: number) => {
    if (!favoritableItems.some((item) => item.id === id)) return;
    const visibleOrder = favoriteItems.map((item) => item.id).filter((favId) => favId !== id);
    const position = Math.max(0, Math.min(index, visibleOrder.length));
    visibleOrder.splice(position, 0, id);
    commit(withHiddenIds(visibleOrder, currentIds()));
  };

  const beginDrag = () => setFrozen(favoriteIds);

  const endDrag = () => setFrozen(null);

  return {
    favoriteItems,
    isFavorite,
    toggleFavorite,
    setFavoritesOrder,
    addFavoriteAt,
    beginDrag,
    endDrag,
    pending,
    resyncing,
  };
};
